import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../../common/colorable-component";
import {
  FilePreviewLocalization,
  FilePreviewSchemeDefinition,
  FilePreviewSchemeName
} from "./constants/file-preview-types";
import { FILE_PREVIEW_SCHEME_CONFIG, FILE_PREVIEW_SCHEME_MAP } from "./constants/file-preview-constants";
import pfx from "../../../utils/style-class-prefix-helper";
import {
  LocalizableComponent,
  Localization,
  setCurrentLocalizationDelegate
} from "../../../common/localizable-component";
import { FILE_PREVIEW_LOCALIZATION } from "./constants/file-preview-i18n";
import { isMobile } from "../../../utils/screen-helper";
import { FilePreviewSchemeConfiguration } from "../../../constants/colors";
import { KeyIds } from "../../../constants/keys";
import { BackgroundColor } from "../../background/constants/background-types";
import { StyledComponent } from "../../../decorators/styled-component";

// import { InitLogging } from "../../../decorators/init-logging";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 */
@Component({
  tag: "envc-eon-ui-file-preview",
  styleUrl: "file-preview.scss",
  shadow: true
})
export class FilePreview
  implements
    ColorableComponent<FilePreviewSchemeConfiguration<FilePreviewSchemeDefinition>>,
    LocalizableComponent<FilePreviewLocalization> {
  // @InitLogging() sentryTransaction;
  schemeConfig: FilePreviewSchemeConfiguration<FilePreviewSchemeDefinition> = FILE_PREVIEW_SCHEME_CONFIG;
  schemeMap = FILE_PREVIEW_SCHEME_MAP;

  localization: Localization<FilePreviewLocalization> = FILE_PREVIEW_LOCALIZATION;
  currentLocalization: FilePreviewLocalization;

  /**
   * Fires an event when the upload should be canceled BEFORE the upload is in progress
   */
  @Event() cancel: EventEmitter<number>;

  /**
   * Fires an event when the upload progress should be aborted
   */
  @Event() abortUpload: EventEmitter<number>;

  /**
   * Fires an event when the upload should be retired after an error
   */
  @Event() retryUpload: EventEmitter<number>;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: FilePreviewSchemeDefinition;

  @State() isMobileDevice: boolean;
  @State() isDesktop: boolean;
  @State() previewImage: string;
  @State() previewIcon: string;
  @State() fileName: String;
  @State() fileSize: String;
  @State() progressElement: HTMLEnvcEonUiProgressCircleElement;
  @State() showCircle: boolean;
  @State() showIcon: boolean;
  @State() fileIsImage: boolean;

  /**
   * The file object to be previewed
   */
  @Prop() file: File;
  /**
   * A unique identifier for the file
   */
  @Prop() fileId: number;

  /**
   * Controls if the upload is in progress
   */
  @Prop({ reflect: true }) uploadInProgress: boolean;

  /**
   * The progress in numbers
   */
  @Prop({ reflect: true }) progress: number = 0;

  /**
   * Controls if the upload has an error
   */
  @Prop() uploadError: boolean;

  /**
   * Controls if the upload is complete
   */
  @Prop() uploadComplete: boolean;

  /**
   * Controls if the progress-circle should be shown or not
   */
  @Prop() hideProgress: boolean;

  /**
   * The request associated with the file
   */
  @Prop() request: XMLHttpRequest;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEnvcEonUiFilePreviewElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: FilePreviewSchemeName;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  componentWillLoad() {
    this.applyScheme();
    this.setCurrentLocalization();
    this.checkFile();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.onViewportSizeChange();
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  @Listen("resize", { target: "window" })
  onViewportSizeChange() {
    this.isMobileDevice = isMobile();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("file")
  checkFile() {
    this.getPreview();
    this.getFileSize();
    /**
     * FileName character limitation has been set to 140
     * fileName character length exceeds 140 count then fileName is trimmed in 4 sections as follows:
     * <fileName(137 characters)><...(ellipsis)><fileName(remaining 3 characters)>.<fileExtension>
     */
    if (this.file.name.length >= 140) {
      const fileExtension = this.file.name.split(".").pop();
      const optimizedFileName = this.file.name.replace(/\.[^/.]+$/, "");
      this.fileName =
        optimizedFileName.substring(0, 137) +
        "..." +
        optimizedFileName.substring(0, 140).slice(-3) +
        `.${fileExtension}`;
    } else {
      this.fileName = this.file.name;
    }
    this.showIcon = true;
  }

  /**
   * Sets the file preview image according to the file ending
   */
  private getPreview() {
    if (!this.file) {
      return;
    }
    const fileType = this.file.type;
    //reset the image and the icon
    this.previewIcon = undefined;
    this.previewImage = undefined;

    if (fileType.indexOf("image") > -1) {
      //if the type is an image, show the image preview
      this.fileIsImage = true;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewImage = e.target.result as string;
      };

      reader.readAsDataURL(this.file); // convert to base64 string
    } else if (fileType.indexOf("pdf") > -1) {
      this.fileIsImage = false;
      this.previewIcon = "file_pdf";
    } else {
      this.fileIsImage = false;
      this.previewIcon = "file";
    }
  }

  /**
   * Gets the correct file size with extension
   */
  private getFileSize() {
    let size = this.file?.size;
    //gets the exact file Size
    var fSExt = new Array("Bytes", "KB", "MB", "GB"),
      i = 0;
    while (size > 900) {
      size /= 1024;
      i++;
    }
    this.fileSize = Math.round(size * 100) / 100 + " " + fSExt[i];
  }

  private handleIconClick() {
    if (this.uploadInProgress) {
      this.abortUpload.emit(this.fileId);
      this.progress = 0;
      this.uploadInProgress = false;
    }
    if (this.uploadError) {
      //if there is an error, retry the fileupload
      this.retryUpload.emit(this.fileId);
    }
  }

  /**
   * Gets the correct circle name according to the current status
   */
  private get cicleIconName() {
    if (this.uploadInProgress) {
      return "close";
    }
    if (this.uploadError) {
      return "reload";
    }
    if (this.uploadComplete) {
      return "radio_checkbox_tick";
    }
    return "";
  }

  @Watch("uploadComplete")
  @Watch("uploadInProgress")
  @Watch("uploadError")
  handleCircle() {
    /* if (this.hideProgress) {
      this.showCircle = false;
      this.showIcon = false;
    } else */ if (
      this.uploadComplete ||
      this.uploadInProgress ||
      this.uploadError
    ) {
      this.showCircle = true;
      this.showIcon = false;
    } else {
      this.showCircle = false;
      this.showIcon = true;
    }
  }

  handleCancelIconClick() {
    this.cancel.emit(this.fileId);
  }

  handleCancelIconKeyDown(e: KeyboardEvent) {
    if (e.key == KeyIds.SPACE || e.key == KeyIds.ENTER) {
      e.preventDefault();
      this.handleCancelIconClick();
    }
  }

  render() {
    return (
      <Host>
        <div class={pfx({ fileupload__preview: true })} inner-background={this.activeScheme.boxBackground}>
          <div class={pfx({ fileupload__preview__box: true })}>
            {this.previewImage && (
              <div class={pfx({ fileupload__preview__image: true })}>
                <envc-eon-ui-image src={this.previewImage} useAsBackground={true}></envc-eon-ui-image>{" "}
              </div>
            )}
            {this.previewIcon && (
              <div class={pfx({ "fileupload__preview__file-icon": true })}>
                <envc-eon-ui-icon name={this.previewIcon}></envc-eon-ui-icon>
              </div>
            )}

            <div class={pfx({ fileupload__preview__description: true })}>
              <envc-eon-ui-text
                textStyle={this.isMobileDevice ? "copy-small" : "copy"}
                class={pfx({ fileupload__preview__description__name: true })}
              >
                {this.fileName}
              </envc-eon-ui-text>
              {!this.uploadComplete && (
                <envc-eon-ui-text textStyle="copy-small" class={pfx({ fileupload__preview__description__size: true })}>
                  {this.fileSize}
                </envc-eon-ui-text>
              )}
              {this.uploadComplete && (
                <envc-eon-ui-text textStyle="copy-small">{this.currentLocalization.completedString}</envc-eon-ui-text>
              )}
              {this.uploadError && (
                <envc-eon-ui-text textStyle="copy-small">{this.currentLocalization.errorMessageString}</envc-eon-ui-text>
              )}
            </div>
            <div class={pfx({ "fileupload__preview__icon-wrapper": true })}>
              {this.showIcon && (
                <div
                  class={pfx({ "fileupload__preview__icon fileupload__preview__icon--cancel": true })}
                  tabindex="0"
                  role="button"
                  onKeyDown={(event: KeyboardEvent) => this.handleCancelIconKeyDown(event)}
                >
                  <envc-eon-ui-icon name="close" onClick={() => this.handleCancelIconClick()}></envc-eon-ui-icon>
                </div>
              )}
              {this.showCircle && (
                <envc-eon-ui-progress-circle
                  class={pfx({ fileupload__preview__icon: true })}
                  ref={(el) => (this.progressElement = el)}
                  progress={this.progress}
                  onIconClick={() => this.handleIconClick()}
                  tabindex={this.uploadComplete ? "-1" : "0"}
                  role="button"
                  onKeyDown={(event: KeyboardEvent) => this.handleCancelIconKeyDown(event)}
                  icon-name={this.cicleIconName}
                  error={this.uploadError}
                ></envc-eon-ui-progress-circle>
              )}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
